import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

export type TenantRole = 'owner' | 'admin' | 'pharmacist' | 'member' | 'viewer';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  settings: Record<string, unknown>;
  created_at: string;
  merged_into_tenant_id?: string | null;
  merged_at?: string | null;
  role?: TenantRole;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  loading: boolean;
  canManageCurrentTenant: boolean;
  currentTenantReadOnly: boolean;
  switchTenant: (tenantId: string) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  currentTenant: null,
  tenants: [],
  loading: true,
  canManageCurrentTenant: false,
  currentTenantReadOnly: false,
  switchTenant: () => {},
  refreshTenants: async () => {},
});

export const useTenant = () => useContext(TenantContext);

const TENANT_KEY = 'precidose_tenant_id';

export const isTenantStateLoading = (
  requestLoading: boolean,
  userId: string | null | undefined,
  loadedUserId: string | null,
) => requestLoading || Boolean(userId && loadedUserId !== userId);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const refreshTenants = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!user) {
      setTenants([]);
      setCurrentTenant(null);
      setLoadedUserId(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setTenants([]);
    setCurrentTenant(null);
    try {
      const { default: api } = await import('../services/api');
      const res = await api.get<Tenant[]>('/api/tenants/');
      if (requestId !== requestIdRef.current) return;
      const list = res.data.filter((tenant) => tenant.status !== 'merged');
      setTenants(list);
      const savedId = localStorage.getItem(TENANT_KEY);
      const saved = list.find(t => t.id === savedId);
      const fallback = list.find(t => t.status === 'active') || list[0] || null;
      const next = saved || fallback;
      setCurrentTenant(next);
      if (next) localStorage.setItem(TENANT_KEY, next.id);
      else localStorage.removeItem(TENANT_KEY);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setTenants([]);
      setCurrentTenant(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadedUserId(user.id);
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => { refreshTenants(); }, [refreshTenants]);

  const switchTenant = (tenantId: string) => {
    const t = tenants.find(x => x.id === tenantId);
    if (t) {
      setCurrentTenant(t);
      localStorage.setItem(TENANT_KEY, tenantId);
    }
  };

  return (
    <TenantContext.Provider value={{
      currentTenant,
      tenants,
      loading: isTenantStateLoading(loading, user?.id, loadedUserId),
      canManageCurrentTenant: currentTenant?.role === 'owner' || currentTenant?.role === 'admin',
      currentTenantReadOnly: currentTenant?.status === 'suspended',
      switchTenant,
      refreshTenants,
    }}>
      {children}
    </TenantContext.Provider>
  );
};
